import React, { useEffect, useRef } from 'react';
import { statusKeys } from './consts';
import { LuRedo2, LuTrash2, LuUndo2 } from 'react-icons/lu';
import { LuInfo } from 'react-icons/lu';
import Switch from 'react-switch';
import reportStore from '../../store';
import { Box, Button, Flex, Text } from '@mantine/core';

interface ReportStatus {
  submit_status?: string;
}

interface Fund {
  fund_id: string;
  fund_name: string;
  report_status?: ReportStatus;
}

interface FundsProps {
  onChangeDataKey: (fundId: string) => void;
  currentDataKey: string;
  funds: Record<string, Fund>;
  isStaticTemplate?: boolean;
  onClearStaticData: () => void;
  isAIF001?: boolean;
  undo: () => void;
  redo: () => void;
  canRedo: boolean;
  canUndo: boolean;
  handleChangeIsMultiFunds: (checked: boolean) => void;
  isMultiFunds: boolean;
  isViewer?: boolean;
}

export function Funds({
  onChangeDataKey,
  currentDataKey,
  funds,
  isStaticTemplate = false,
  onClearStaticData,
  isAIF001,
  undo,
  redo,
  canRedo,
  canUndo,
  handleChangeIsMultiFunds,
  isMultiFunds,
  isViewer = false
}: FundsProps) {
  const tabsRef = useRef<(HTMLElement | null)[]>([]);
  // @ts-ignore
  const { isLockedPeriod } = reportStore;

  useEffect(() => {
    if (tabsRef.current[currentDataKey]) {
      tabsRef.current[currentDataKey].scrollIntoView({
        behavior: 'smooth',
        inline: 'center'
      });
    }
  }, [currentDataKey]);

  return (
    <Box id="report-funds-root" mb="24px">
      {isStaticTemplate && (
        <Flex id="report-funds-static-template-header" align="center" justify="space-between">
          <Text fz="18px" lh="24px" fw={600}>
            Static Templates
          </Text>

          <Flex id="report-funds-static-template-actions" align="center" gap="16px">
            <Flex id="report-funds-history-actions" align="center" gap="10px">
              <LuUndo2
                id="report-funds-undo-button"
                fontSize="16px"
                onClick={undo}
                color={canUndo ? '#228be6' : '#909296'}
              />
              <LuRedo2
                id="report-funds-redo-button"
                fontSize="16px"
                onClick={redo}
                color={canRedo ? '#228be6' : '#909296'}
              />
            </Flex>
            {!isAIF001 && (
              <Flex id="report-funds-multi-fund-toggle" align="center" gap="10px">
                <Text fz="14px" lh="20px">
                  Multi-Fund View
                </Text>
                <Switch
                  id="report-funds-multi-fund-switch"
                  uncheckedIcon={false}
                  width={42}
                  height={20}
                  onChange={handleChangeIsMultiFunds}
                  checkedIcon={false}
                  handleDiameter={15}
                  checked={isMultiFunds}
                  onColor="#228be6"
                  offColor="#E9ECEF"
                />
              </Flex>
            )}
            {!isLockedPeriod && (
              <Button
                id="report-funds-clear-button"
                leftSection={<LuTrash2 />}
                size="xs"
                variant="subtle"
                onClick={onClearStaticData}
              >
                Clear
              </Button>
            )}
          </Flex>
        </Flex>
      )}
      {!isAIF001 && !isMultiFunds && (
        <Flex
          id="report-funds-tabs"
          align="center"
          pb="5px"
          // @ts-ignore
          sx={() => ({
            overflow: 'auto'
          })}
        >
          {Object.values(funds).map((item: Fund) => (
            <Flex
              component="button"
              id={`report-funds-tab-${item.fund_id}`}
              onClick={() => onChangeDataKey(item.fund_id)}
              key={item.fund_id}
              ref={(el: HTMLElement | null) => {
                tabsRef.current[item.fund_id as any] = el;
              }}
              className={currentDataKey === item.fund_id ? 'active' : ''}
              p="12px"
              c="black"
              fz="14px"
              fw={500}
              bg="white"
              align="center"
              gap="8px"
              // @ts-ignore
              sx={() => ({
                whiteSpace: 'nowrap',
                borderBottom:
                  currentDataKey === item.fund_id ? '2px solid #228be6' : '2px solid #dee2e6'
              })}
            >
              {!isViewer && item.report_status?.submit_status === statusKeys.ERROR && (
                <LuInfo color="#E03131" />
              )}
              <p>{item.fund_name}</p>
            </Flex>
          ))}
        </Flex>
      )}
    </Box>
  );
}
